<script setup>
// A GAVETA DAS PENDÊNCIAS (18/10/2026): o que espera uma decisão, agrupado, com a decisão ali
// mesmo quando cabe (o aviso ao cliente se aprova, edita ou descarta aqui) e o atalho certo
// quando não cabe (a resposta da IA se decide na conversa, onde está o contexto).
//
// "Meus" primeiro: o advogado vê o que é dele. O administrador alterna para "do escritório".
//
// A CAIXA ÚNICA (fatia 4, 20/10/2026 — docs/produto/ESTUDO-COPILOTO-E-CAIXA-UNICA.md): cada item diz há quanto
// tempo espera e se passou do prazo; o andamento que pede ação se decide aqui ("Já vi", "Criar tarefa"); cada
// pessoa adia o seu até amanhã; atalhos de teclado; e o administrador vê os números e escolhe o substituto.
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import pendenciasAPI from 'dashboard/api/pendencias';
import followUpCadenceRunsAPI from 'dashboard/api/followUpCadenceRuns';
import GavetaLateral from 'dashboard/components-next/minutas/GavetaLateral.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import AvisoParaAprovar from 'dashboard/components-next/avisos/AvisoParaAprovar.vue';
import NumerosDasPendencias from './NumerosDasPendencias.vue';
import { nomeCurto } from 'dashboard/helper/avatar';
import { horaEscritorio } from 'dashboard/helper/fusoEscritorio';
import DigitosRolantes from 'dashboard/components-next/marca/DigitosRolantes.vue';
import {
  CHAVE_AVISO_DESLIGADO,
  destinoDo,
  gruposComItens,
  haQuanto,
  tarefaDoAndamento,
} from './pendencias';

const props = defineProps({
  aberta: { type: Boolean, default: false },
  // O administrador sem nada "dele" abre direto no escritório.
  escopoInicial: { type: String, default: 'meus' },
  // Aberta pelo resumo da manhã: mostra o "Bom dia" com o que é da pessoa hoje.
  daManha: { type: Boolean, default: false },
});
const emit = defineEmits(['fechar', 'mudou']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const P = 'PENDENCIAS';
const lista = ref(null);
const usuario = useMapGetter('getCurrentUser');
// O "Bom dia" (resumo da manhã): a data por extenso e o que espera a pessoa, em selos — o que passou do prazo
// primeiro, em âmbar, porque é por onde começar.
const hojeEmFrase = computed(() => {
  const frase = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return frase.charAt(0).toUpperCase() + frase.slice(1);
});

// O nome curto da casa (`nomeCurto`): "Dra. Jullya Andrade" é "Dra. Jullya" — o `split` dizia "Bom dia, Dra.!".
const primeiroNome = computed(() =>
  nomeCurto(usuario.value?.available_name || usuario.value?.name)
);
// A saudação segue o relógio do ESCRITÓRIO, como no Seu dia: o Bom dia abre no primeiro acesso, que pode ser às
// 15h — e "Bom dia" às 18h soava como erro. O nome da peça continua "Bom dia".
const saudacaoDaManha = computed(() => {
  const hora = Number(horaEscritorio(new Date().toISOString()).slice(0, 2));
  let chave = 'MANHA_TITULO';
  if (hora >= 18) chave = 'MANHA_TITULO_NOITE';
  else if (hora >= 12) chave = 'MANHA_TITULO_TARDE';
  return t(`${P}.${chave}`, { nome: primeiroNome.value });
});
const ocupado = ref(null);

// "Não quero mais que abra sozinho": a caixinha do Bom dia grava na hora, nas preferências da pessoa (vale em
// qualquer computador). O botão âmbar e o sininho seguem como sempre. Religa no Perfil > Preferências de notificação.
const { uiSettings, updateUISettings } = useUISettings();
const avisoDesligado = computed({
  get: () => !!uiSettings.value?.[CHAVE_AVISO_DESLIGADO],
  set: valor => updateUISettings({ [CHAVE_AVISO_DESLIGADO]: valor }),
});

const dados = ref(null);
const carregando = ref(false);
const erro = ref(false);
const escopo = ref('meus');

const grupos = computed(() => gruposComItens(dados.value?.grupos || []));

// Só o selo dos ATRASADOS (Victor, 30/10/2026: "tira"): os selos por tipo repetiam, um palmo acima, os números do
// mapa logo abaixo do cartão — a mesma informação duas vezes seguidas. O detalhe por tipo mora no mapa, que leva ao
// grupo; o cartão diz o total e por onde começar.
const selosDaManha = computed(() => {
  const escalados = grupos.value.reduce(
    (soma, g) => soma + g.itens.filter(i => i.escalado).length,
    0
  );
  if (!escalados) return [];
  return [
    {
      chave: 'escalados',
      alerta: true,
      texto: t(`${P}.MANHA_SELOS.ESCALADOS`, { n: escalados }, escalados),
    },
  ];
});

const carregar = async () => {
  carregando.value = !dados.value;
  erro.value = false;
  try {
    const { data } = await pendenciasAPI.lista(escopo.value);
    dados.value = data;
  } catch {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

watch(
  () => props.aberta,
  sim => {
    if (!sim) return;
    escopo.value = props.escopoInicial;
    carregar();
  },
  // Aberta já na montagem (o link do resumo da manhã) também carrega.
  { immediate: true }
);

const trocarEscopo = novo => {
  if (escopo.value === novo) return;
  escopo.value = novo;
  if (novo === 'numeros') return;
  dados.value = null;
  carregar();
};

const escopos = computed(() =>
  dados.value?.pode_ver_escritorio || escopo.value === 'numeros'
    ? ['meus', 'escritorio', 'numeros']
    : ['meus']
);

// Decidiu um aviso: recarrega a lista e avisa o contador (e qualquer tela aberta).
const decidido = async () => {
  await carregar();
  emit('mudou');
  emitter.emit('pendencias:mudou');
};

const destino = (tipo, item) => destinoDo(tipo, item, route.params.accountId);
const tarefa = item => tarefaDoAndamento(item, route.params.accountId);

// Um ícone e um tom por tipo: a gaveta se lê pela forma antes do texto. Tom só onde carrega sentido
// (dinheiro em âmbar, IA em íris, cliente em teal); o resto fica na cor da marca.
const ICONES = {
  aviso_ao_cliente: ['i-lucide-megaphone', 'teal'],
  resposta_da_ia: ['i-lucide-sparkles', 'iris'],
  cobranca: ['i-lucide-receipt', 'ambar'],
  andamento_que_pede_acao: ['i-lucide-scale', ''],
  honorario_a_lancar: ['i-lucide-banknote', 'ambar'],
  retomada: ['i-lucide-repeat', 'iris'],
  tarefa_do_follow_up: ['i-lucide-list-checks', ''],
  lead_parado: ['i-lucide-hourglass', 'ambar'],
};
const icone = tipo => ICONES[tipo]?.[0] || 'i-lucide-circle-dot';
// O rosto do cartão: as iniciais de quem é o item, na cor do tipo.
const FALAS = ['resposta_da_ia', 'lead_parado', 'cobranca', 'retomada'];
const ehFala = tipo => FALAS.includes(tipo);
const TECLA_DO_DESFAZER = 'Z';
const ATALHOS = [
  { teclas: ['J', 'K'], rotulo: 'TECLA_NAVEGAR' },
  { teclas: ['Enter'], rotulo: 'TECLA_ABRIR' },
  { teclas: ['V'], rotulo: 'TECLA_VI' },
  { teclas: ['A'], rotulo: 'TECLA_ADIAR' },
  { teclas: [TECLA_DO_DESFAZER], rotulo: 'TECLA_DESFAZER' },
];
const TOM_DO_AVATAR = {
  aviso_ao_cliente: 'teal',
  resposta_da_ia: 'iris',
  cobranca: 'ambar',
  andamento_que_pede_acao: 'azul',
  honorario_a_lancar: 'ambar',
  retomada: 'iris',
  tarefa_do_follow_up: 'azul',
  lead_parado: 'ambar',
};
const tomDoAvatar = tipo => TOM_DO_AVATAR[tipo] || 'azul';
// Nome vazio ou só pontuação (o "." que chega do WhatsApp) não é nome.
const temNome = nome => /[\p{L}\p{N}]/u.test(nome || '');
const nomeDe = nome => (temNome(nome) ? nome : t(`${P}.SEM_NOME`));
const iniciais = nome => {
  if (!temNome(nome)) return '?';
  const partes = nome
    .trim()
    .split(/\s+/)
    .filter(p => /[\p{L}]/u.test(p));
  const primeira = partes[0]?.[0] || '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return `${primeira}${ultima}`.toUpperCase() || '?';
};
const irPara = tipo =>
  document
    .getElementById(`pend-${tipo}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const tom = tipo =>
  ICONES[tipo]?.[1] ? `advup-secao__ico--${ICONES[tipo][1]}` : '';

const esperando = iso => {
  const ha = haQuanto(iso);
  return ha ? t(`${P}.HA.${ha.unidade}`, { n: ha.n }, ha.n) : '';
};

const itensNaTela = () => [
  ...(lista.value?.querySelectorAll('[data-item]') || []),
];

// Depois de decidir pelo teclado, o foco fica no item que tomou o lugar (senão ia para a página e o próximo
// atalho não chegava aqui).
const focarDepois = async indice => {
  await decidido();
  const todos = itensNaTela();
  if (indice !== undefined && todos.length)
    todos[Math.min(indice, todos.length - 1)].focus();
};

// O DESFAZER (20/10/2026): "Já vi" e "Adiar" num clique errado tinham ida sem volta — o item sumia. Agora uma barra
// no pé da gaveta oferece desfazer por alguns segundos (Gmail, Linear); o servidor só aceita de quem decidiu e logo
// depois. Uma barra por vez: decidir outro item troca a oferta.
const SEGUNDOS_DO_DESFAZER = 8;
const oferta = ref(null);
let relogioDoDesfazer = null;
const fecharOferta = () => {
  clearTimeout(relogioDoDesfazer);
  oferta.value = null;
};
const oferecerDesfazer = dadosDaOferta => {
  clearTimeout(relogioDoDesfazer);
  oferta.value = { ...dadosDaOferta, vez: Date.now() };
  relogioDoDesfazer = setTimeout(fecharOferta, SEGUNDOS_DO_DESFAZER * 1000);
};
onBeforeUnmount(() => clearTimeout(relogioDoDesfazer));
watch(
  () => props.aberta,
  sim => {
    if (!sim) fecharOferta();
  }
);

const desfazer = async () => {
  const atual = oferta.value;
  if (!atual || ocupado.value) return;
  fecharOferta();
  ocupado.value = atual.item.chave;
  try {
    if (atual.acao === 'viu')
      await pendenciasAPI.desfazerAndamento(atual.item.id);
    else await pendenciasAPI.desadiar(atual.tipo, atual.item.id);
    useAlert(t(`${P}.DESFAZER.FEITO`));
    await decidido();
  } catch (falha) {
    useAlert(
      falha?.response?.status === 409
        ? t(`${P}.DESFAZER.TARDE`)
        : t(`${P}.ERRO_DECIDIR`)
    );
  } finally {
    ocupado.value = null;
  }
};

// O andamento que pede ação se decide aqui: não tinha tela nenhuma (85 parados na A&T em 20/10).
const jaVi = async (item, indice) => {
  if (ocupado.value) return;
  ocupado.value = item.chave;
  try {
    await pendenciasAPI.decidirAndamento(item.id, 'viu');
    oferecerDesfazer({ acao: 'viu', tipo: 'andamento_que_pede_acao', item });
    await focarDepois(indice);
  } catch (falha) {
    const jaDecidido = falha?.response?.status === 409;
    useAlert(jaDecidido ? t(`${P}.JA_DECIDIDO`) : t(`${P}.ERRO_DECIDIR`));
    // Outra pessoa decidiu antes: a lista se atualiza e o item some.
    if (jaDecidido) await decidido();
  } finally {
    ocupado.value = null;
  }
};

// O HONORÁRIO TEM SAÍDA (23/10/2026, Victor: "só dava a opção de ir lá"). "Já lancei" ou "Não é honorário" —
// dinheiro não vence sozinho, então a decisão é de gente, em um clique. Sem "desfazer": o livro registra.
const decidirHonorario = async (item, acao) => {
  if (ocupado.value) return;
  ocupado.value = item.chave;
  try {
    await pendenciasAPI.decidirHonorario(item.id, acao);
    useAlert(
      t(
        `${P}.GRUPOS.honorario_a_lancar.${acao === 'lancou' ? 'LANCADO' : 'DISPENSADO'}`
      )
    );
  } catch (falha) {
    useAlert(
      falha?.response?.status === 409
        ? t(`${P}.JA_DECIDIDO`)
        : t(`${P}.ERRO_DECIDIR`)
    );
  } finally {
    ocupado.value = null;
    await decidido();
  }
};

// A RETOMADA SE DECIDE AQUI (22/10/2026): na conversa com responsável, o follow-up só sugere — a pessoa lê a mensagem
// e a última fala da equipe e escolhe, sem abrir a conversa. Enviar não tem "desfazer": sai (ou entra na fila do
// horário de envio). Se a vez virou do escritório enquanto esperava, o servidor não envia e diz por quê.
const decidirRetomada = async (item, enviar) => {
  if (ocupado.value) return;
  ocupado.value = item.chave;
  try {
    if (enviar) await followUpCadenceRunsAPI.approveStep(item.id);
    else await followUpCadenceRunsAPI.rejectStep(item.id);
    useAlert(t(`${P}.GRUPOS.retomada.${enviar ? 'ENVIADA' : 'DESCARTADA'}`));
  } catch (falha) {
    const motivo = falha?.response?.data?.error;
    useAlert(
      motivo?.startsWith('não enviada') ? motivo : t(`${P}.ERRO_DECIDIR`)
    );
  } finally {
    ocupado.value = null;
    await decidido();
  }
};

const adiar = async (tipo, item, indice) => {
  if (ocupado.value) return;
  ocupado.value = item.chave;
  try {
    await pendenciasAPI.adiar(tipo, item.id);
    oferecerDesfazer({ acao: 'adiar', tipo, item });
    await focarDepois(indice);
  } catch {
    useAlert(t(`${P}.ERRO_DECIDIR`));
  } finally {
    ocupado.value = null;
  }
};

// ATALHOS (como a triagem do Linear): J/K andam entre os itens, Enter abre, V marca "já vi" no andamento,
// A adia. Só com o foco num item, e nunca dentro de um campo de texto (o aviso se edita na gaveta).

const focar = passo => {
  const todos = itensNaTela();
  if (!todos.length) return;
  const atual = todos.indexOf(document.activeElement);
  const proximo = Math.min(todos.length - 1, Math.max(0, atual + passo));
  todos[atual === -1 ? 0 : proximo].focus();
};

const naTecla = evento => {
  if (evento.target.closest('input, textarea, [contenteditable="true"]'))
    return;
  if (evento.metaKey || evento.ctrlKey || evento.altKey) return;
  const tecla = evento.key.toLowerCase();
  if (tecla === 'z' && oferta.value) {
    evento.preventDefault();
    desfazer();
    return;
  }
  if (tecla === 'j' || tecla === 'k') {
    evento.preventDefault();
    focar(tecla === 'j' ? 1 : -1);
    return;
  }
  const no = evento.target.closest('[data-item]');
  if (!no) return;
  const { tipo, indice } = no.dataset;
  const posicao = itensNaTela().indexOf(no);
  const item = grupos.value.find(g => g.tipo === tipo)?.itens[Number(indice)];
  if (!item) return;
  if (tecla === 'enter' && no === evento.target) {
    const alvo = destino(tipo, item);
    if (alvo) {
      evento.preventDefault();
      emit('fechar');
      router.push(alvo);
    }
  } else if (tecla === 'v' && tipo === 'andamento_que_pede_acao') {
    evento.preventDefault();
    jaVi(item, posicao);
  } else if (tecla === 'a' && escopo.value === 'meus') {
    evento.preventDefault();
    adiar(tipo, item, posicao);
  }
};

const quando = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
</script>

<template>
  <GavetaLateral
    :aberta="aberta"
    :titulo="t(`${P}.TITULO`)"
    :sub="t(`${P}.SUB`)"
    icone="i-ph-hand-pointing-fill"
    larga
    :ocupada="carregando"
    foco-no-titulo
    @fechar="emit('fechar')"
  >
    <div
      ref="lista"
      class="pend"
      :class="{ 'pend--manha': daManha && escopo === 'meus' }"
      @keydown="naTecla"
    >
      <div
        v-if="escopos.length > 1"
        class="advup-seg pend__escopo"
        role="group"
        :aria-label="t(`${P}.ESCOPO`)"
      >
        <button
          v-for="e in escopos"
          :key="e"
          type="button"
          class="advup-seg__btn"
          :class="{ 'advup-seg__btn--on': escopo === e }"
          :aria-pressed="escopo === e"
          @click="trocarEscopo(e)"
        >
          {{ t(`${P}.ESCOPOS.${e}`) }}
        </button>
      </div>

      <NumerosDasPendencias v-if="escopo === 'numeros'" />

      <template v-else>
        <!-- A CENA DA MANHÃ (30/10/2026, padrão do redesenho — docs/movimento/BRIEFING-CENA.md): o sol nasce de
             dentro do ladrilho, a data sobe, a saudação nasce da linha, o total rola como odômetro, os selos chegam
             um a um, a caixa por último e uma luz atravessa o cartão uma vez. O resto da gaveta responde meio
             compasso depois. Nada se repete. -->
        <section
          v-if="daManha && dados && escopo === 'meus'"
          class="pend__manha"
          role="status"
        >
          <span class="pend__manha-luz" aria-hidden="true" />
          <span class="pend__manha-sol" aria-hidden="true">
            <i class="i-lucide-sunrise" />
          </span>
          <div class="pend__manha-corpo">
            <p class="pend__manha-data">{{ hojeEmFrase }}</p>
            <h3 class="pend__manha-tit">
              <span class="pend__manha-revela">{{ saudacaoDaManha }}</span>
            </h3>
            <p class="pend__manha-sub">
              <DigitosRolantes
                v-if="dados.total"
                :texto="t(`${P}.MANHA_SUB`, { n: dados.total }, dados.total)"
              />
              <template v-else>{{ t(`${P}.MANHA_VAZIO`) }}</template>
            </p>
            <ul v-if="selosDaManha.length" class="pend__manha-selos">
              <li
                v-for="(selo, i) in selosDaManha"
                :key="selo.chave"
                class="pend__manha-selo"
                :style="{ '--i': i }"
                :class="{ 'pend__manha-selo--alerta': selo.alerta }"
              >
                <i
                  v-if="selo.alerta"
                  class="i-lucide-clock-alert"
                  aria-hidden="true"
                />
                {{ selo.texto }}
              </li>
            </ul>
            <label class="pend__manha-aviso">
              <input
                v-model="avisoDesligado"
                type="checkbox"
                class="advup-caixa"
              />
              <span>{{ t(`${P}.MANHA_NAO_ABRIR`) }}</span>
            </label>
          </div>
        </section>

        <p v-if="dados?.substituto && escopo === 'meus'" class="pend__selo">
          <i class="i-lucide-life-buoy" aria-hidden="true" />
          {{ t(`${P}.SUBSTITUTO_SELO`) }}
        </p>

        <EsqueletoLista
          v-if="carregando"
          :linhas="3"
          :altura="140"
          :rotulo="t(`${P}.CARREGANDO`)"
        />

        <div v-else-if="erro" class="advup-vazio advup-vazio--erro">
          <span class="advup-vazio__icone" aria-hidden="true">
            <i class="i-lucide-triangle-alert" />
          </span>
          <p class="advup-vazio__tit">{{ t(`${P}.ERRO`) }}</p>
          <button
            type="button"
            class="advup-btn advup-vazio__acao"
            @click="carregar"
          >
            <i class="i-lucide-rotate-ccw" aria-hidden="true" />
            {{ t(`${P}.TENTAR_DE_NOVO`) }}
          </button>
        </div>

        <div v-else-if="dados && !grupos.length" class="advup-vazio">
          <span class="advup-vazio__icone" aria-hidden="true">
            <i class="i-ph-check-circle" />
          </span>
          <p class="advup-vazio__tit">{{ t(`${P}.VAZIO`) }}</p>
        </div>

        <!-- De relance: um selo por grupo, com o número; o clique leva ao grupo. -->
        <nav
          v-if="grupos.length > 1"
          class="pend__mapa"
          :aria-label="t(`${P}.MAPA`)"
        >
          <button
            v-for="grupo in grupos"
            :key="grupo.tipo"
            type="button"
            class="pend__mapa-item"
            @click="irPara(grupo.tipo)"
          >
            <span
              class="pend__mapa-ico advup-secao__ico"
              :class="tom(grupo.tipo)"
              aria-hidden="true"
            >
              <i :class="icone(grupo.tipo)" />
            </span>
            <span class="pend__mapa-n">{{ grupo.itens.length }}</span>
            <span class="pend__mapa-nome">{{
              t(`${P}.GRUPOS.${grupo.tipo}.CURTO`, grupo.itens.length)
            }}</span>
          </button>
        </nav>

        <section
          v-for="grupo in grupos"
          :id="`pend-${grupo.tipo}`"
          :key="grupo.tipo"
          class="advup-secao pend__grupo"
        >
          <h3 class="advup-secao__tit">
            <span
              class="advup-secao__ico"
              :class="tom(grupo.tipo)"
              aria-hidden="true"
            >
              <i :class="icone(grupo.tipo)" />
            </span>
            {{ t(`${P}.GRUPOS.${grupo.tipo}.TITULO`) }}
            <span class="ficha-pill ficha-pill--ambar">{{
              grupo.itens.length
            }}</span>
          </h3>
          <p class="pend__dica">{{ t(`${P}.GRUPOS.${grupo.tipo}.DICA`) }}</p>

          <ul v-if="grupo.tipo === 'aviso_ao_cliente'" class="pend__lista">
            <template v-for="aviso in grupo.itens" :key="aviso.id">
              <li
                v-if="aviso.escalado || escopo === 'meus'"
                class="pend__aviso-faixa"
              >
                <span
                  v-if="aviso.escalado"
                  v-tooltip.top="t(`${P}.ESCALADO_DICA`)"
                  class="pend__escalado"
                >
                  <i class="i-lucide-clock-alert" aria-hidden="true" />
                  {{ t(`${P}.ESCALADO`) }}
                </span>
                <button
                  v-if="escopo === 'meus' && aviso.chave"
                  type="button"
                  class="pend__acao"
                  :disabled="ocupado === aviso.chave"
                  @click="adiar(grupo.tipo, aviso)"
                >
                  <i class="i-lucide-alarm-clock" aria-hidden="true" />
                  {{ t(`${P}.ADIAR`) }}
                </button>
              </li>
              <AvisoParaAprovar :aviso="aviso" @decidido="decidido" />
            </template>
          </ul>

          <ul v-else class="pend__lista">
            <li
              v-for="(item, indice) in grupo.itens"
              :key="item.chave || item.id"
              class="pend__item advup-card advup-card--responde"
              :class="{ 'pend__item--escalado': item.escalado }"
              tabindex="0"
              data-item
              :data-tipo="grupo.tipo"
              :data-indice="indice"
            >
              <header class="pend__item-cab">
                <span
                  class="pend__avatar"
                  :class="`pend__avatar--${tomDoAvatar(grupo.tipo)}`"
                  aria-hidden="true"
                >
                  {{ iniciais(item.cliente) }}
                </span>
                <div class="pend__id">
                  <p class="pend__quem">{{ nomeDe(item.cliente) }}</p>
                  <p
                    v-if="item.agente || item.regra || item.regua"
                    class="pend__de"
                  >
                    <i
                      :class="
                        item.processo_id ? 'i-lucide-folder' : 'i-lucide-tag'
                      "
                      aria-hidden="true"
                    />
                    <router-link
                      v-if="item.processo_id"
                      :to="{
                        name: 'legal_process_show',
                        params: {
                          accountId: route.params.accountId,
                          processId: item.processo_id,
                        },
                      }"
                      class="pend__link"
                      @click="emit('fechar')"
                    >
                      {{ item.agente || item.regra || item.regua }}
                    </router-link>
                    <span v-else>{{
                      item.agente || item.regra || item.regua
                    }}</span>
                  </p>
                </div>
                <p
                  class="pend__quando"
                  :title="
                    quando(item.criada_em || item.executar_em || item.desde)
                  "
                >
                  {{
                    item.esperando_desde
                      ? esperando(item.esperando_desde)
                      : quando(item.criada_em || item.executar_em || item.desde)
                  }}
                </p>
              </header>

              <figure
                v-if="item.texto && ehFala(grupo.tipo)"
                class="pend__fala"
              >
                <figcaption class="pend__fala-rot">
                  <i class="i-lucide-message-circle" aria-hidden="true" />
                  {{ t(`${P}.GRUPOS.${grupo.tipo}.FALA`) }}
                </figcaption>
                <blockquote class="pend__fala-txt">{{ item.texto }}</blockquote>
              </figure>
              <p v-else-if="item.texto" class="pend__texto">{{ item.texto }}</p>

              <figure
                v-if="grupo.tipo === 'retomada' && item.ultima_da_equipe"
                class="pend__trecho"
              >
                <figcaption class="pend__trecho-rot">
                  {{ t(`${P}.GRUPOS.retomada.ULTIMA_DA_EQUIPE`) }}
                </figcaption>
                <blockquote>{{ item.ultima_da_equipe }}</blockquote>
              </figure>

              <figure v-if="item.trecho" class="pend__trecho">
                <figcaption class="pend__trecho-rot">
                  {{ t(`${P}.TRECHO`) }}
                </figcaption>
                <blockquote>{{ item.trecho }}</blockquote>
              </figure>

              <footer class="pend__rodape">
                <span
                  v-if="item.escalado"
                  v-tooltip.top="t(`${P}.ESCALADO_DICA`)"
                  class="pend__escalado"
                >
                  <i class="i-lucide-clock-alert" aria-hidden="true" />
                  {{ t(`${P}.ESCALADO`) }}
                </span>
                <div class="pend__acoes">
                  <button
                    v-if="escopo === 'meus' && item.chave"
                    v-tooltip.top="t(`${P}.ADIAR`)"
                    type="button"
                    class="pend__acao pend__acao--ico"
                    :aria-label="t(`${P}.ADIAR`)"
                    :disabled="ocupado === item.chave"
                    @click="adiar(grupo.tipo, item)"
                  >
                    <i class="i-lucide-alarm-clock" aria-hidden="true" />
                  </button>
                  <template v-if="grupo.tipo === 'andamento_que_pede_acao'">
                    <button
                      type="button"
                      class="pend__acao"
                      data-acao="ja-vi"
                      :disabled="ocupado === item.chave"
                      @click="jaVi(item)"
                    >
                      <i class="i-lucide-check" aria-hidden="true" />
                      {{ t(`${P}.JA_VI`) }}
                    </button>
                    <router-link
                      v-if="item.processo_id"
                      :to="tarefa(item)"
                      class="pend__acao"
                      @click="emit('fechar')"
                    >
                      <i class="i-lucide-calendar-plus" aria-hidden="true" />
                      {{ t(`${P}.CRIAR_TAREFA`) }}
                    </router-link>
                  </template>
                  <template v-if="grupo.tipo === 'honorario_a_lancar'">
                    <button
                      type="button"
                      class="pend__acao"
                      data-acao="nao-e-honorario"
                      :disabled="ocupado === item.chave"
                      @click="decidirHonorario(item, 'nao_e')"
                    >
                      <i class="i-lucide-x" aria-hidden="true" />
                      {{ t(`${P}.GRUPOS.honorario_a_lancar.NAO_E`) }}
                    </button>
                    <button
                      type="button"
                      class="pend__acao"
                      data-acao="ja-lancei"
                      :disabled="ocupado === item.chave"
                      @click="decidirHonorario(item, 'lancou')"
                    >
                      <i class="i-lucide-check" aria-hidden="true" />
                      {{ t(`${P}.GRUPOS.honorario_a_lancar.JA_LANCEI`) }}
                    </button>
                  </template>
                  <template v-if="grupo.tipo === 'retomada' && item.texto">
                    <button
                      type="button"
                      class="pend__acao"
                      data-acao="nao-enviar"
                      :disabled="ocupado === item.chave"
                      @click="decidirRetomada(item, false)"
                    >
                      <i class="i-lucide-x" aria-hidden="true" />
                      {{ t(`${P}.GRUPOS.retomada.NAO_ENVIAR`) }}
                    </button>
                    <button
                      type="button"
                      class="pend__acao"
                      data-acao="enviar"
                      :disabled="ocupado === item.chave"
                      @click="decidirRetomada(item, true)"
                    >
                      <i class="i-lucide-send" aria-hidden="true" />
                      {{ t(`${P}.GRUPOS.retomada.ENVIAR`) }}
                    </button>
                  </template>
                  <router-link
                    v-if="destino(grupo.tipo, item)"
                    :to="destino(grupo.tipo, item)"
                    class="advup-btn advup-btn-forte advup-btn--compacto pend__abrir"
                    @click="emit('fechar')"
                  >
                    {{ t(`${P}.GRUPOS.${grupo.tipo}.ABRIR`) }}
                    <i class="i-hugeicons-arrow-right-01" aria-hidden="true" />
                  </router-link>
                </div>
              </footer>
            </li>
          </ul>
        </section>

        <button
          type="button"
          class="pend__novidade"
          @click="emitter.emit('novidade:abrir', 'novidade_caixa_unica_vista')"
        >
          <i class="i-lucide-sparkles" aria-hidden="true" />
          {{ t(`${P}.O_QUE_MUDOU`) }}
        </button>

        <p v-if="grupos.length" class="pend__atalhos">
          <span v-for="atalho in ATALHOS" :key="atalho.rotulo">
            <kbd v-for="tecla in atalho.teclas" :key="tecla">{{ tecla }}</kbd>
            {{ t(`${P}.${atalho.rotulo}`) }}
          </span>
        </p>
      </template>

      <Transition name="pend-desfazer">
        <div
          v-if="oferta"
          :key="oferta.vez"
          class="pend__desfazer"
          role="status"
          aria-live="polite"
          :style="{ '--pend-desfazer-s': `${SEGUNDOS_DO_DESFAZER}s` }"
        >
          <i
            class="pend__desfazer-ico"
            :class="
              oferta.acao === 'viu'
                ? 'i-lucide-check-check'
                : 'i-lucide-clock-3'
            "
            aria-hidden="true"
          />
          <span class="pend__desfazer-texto">
            {{ t(`${P}.DESFAZER.${oferta.acao === 'viu' ? 'VIU' : 'ADIOU'}`) }}
          </span>
          <button
            type="button"
            class="pend__desfazer-btn"
            data-testid="pend-desfazer"
            @click="desfazer"
          >
            <i class="i-lucide-undo-2" aria-hidden="true" />
            {{ t(`${P}.DESFAZER.BOTAO`) }}
            <kbd>{{ TECLA_DO_DESFAZER }}</kbd>
          </button>
          <span class="pend__desfazer-tempo" aria-hidden="true" />
        </div>
      </Transition>
    </div>
  </GavetaLateral>
</template>

<style scoped>
.pend {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}

.pend__escopo {
  align-self: flex-start;
}

.pend__grupo .advup-secao__tit {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
}

.pend__dica {
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
}

.pend__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

.pend__item {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  padding: var(--esp-5);
  outline: none;
}

.pend__lista > li {
  animation: pend-cartao-entra 360ms var(--mov-curva) both;
}

.pend__lista > li:nth-child(2) {
  animation-delay: 50ms;
}

.pend__lista > li:nth-child(3) {
  animation-delay: 100ms;
}

.pend__lista > li:nth-child(4) {
  animation-delay: 150ms;
}

.pend__lista > li:nth-child(n + 5) {
  animation-delay: 200ms;
}

@keyframes pend-cartao-entra {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pend__lista > li {
    animation: none;
  }
}

.pend__item:focus-visible {
  box-shadow:
    0 0 0 2px rgb(var(--solid-1)),
    0 0 0 4px rgb(var(--blue-8));
}

/* Passou do prazo: um trilho âmbar na borda de dentro, sem trocar a forma do cartão. */
.pend__item--escalado {
  box-shadow:
    inset 3px 0 0 rgb(var(--amber-9)),
    0 1px 2px rgb(15 23 42 / 0.06);
}

.pend__item-cab {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
}

.pend__item-cab .pend__id {
  flex: 1;
}

.pend__id {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.pend__quem {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  color: rgb(var(--slate-12));
  font-size: var(--txt-bloco);
  font-weight: var(--peso-titulo);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pend__de {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  min-width: 0;
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
}

.pend__de > i {
  flex: none;
  width: var(--ico-1);
  height: var(--ico-1);
  color: rgb(var(--slate-10));
}

.pend__link {
  overflow: hidden;
  color: rgb(var(--blue-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pend__link:hover {
  text-decoration: underline;
}

.pend__de > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pend__quando {
  flex: none;
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
}

/* O que a IA leu: é o conteúdo do cartão, lido como texto — não como caixa. */
.pend__texto {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: var(--txt-lista);
  line-height: var(--linha-normal);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* De onde a IA tirou: a prova, discreta, com o nome do que é. */
.pend__trecho {
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--slate-2));
  box-shadow: inset 2px 0 0 rgb(var(--slate-6));
}

.pend__trecho-rot {
  margin-bottom: 2px;
  color: rgb(var(--slate-10));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
}

.pend__trecho blockquote {
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  font-style: italic;
  line-height: var(--linha-normal);
  overflow-wrap: anywhere;
}

.pend__rodape {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  flex-wrap: wrap;
  padding-top: var(--esp-3);
  border-top: 1px solid rgb(var(--slate-4));
}

.pend__escalado {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 3px 9px;
  border-radius: 999px;
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-11));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
}

.pend__escalado > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.pend__acoes {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--esp-1);
  margin-inline-start: auto;
  flex-wrap: wrap;
}

/* Ação secundária: texto com ícone, sem caixa. Um só botão forte por cartão. */
.pend__acao {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 var(--esp-3);
  border: 0;
  border-radius: var(--r-3);
  background: transparent;
  color: rgb(var(--slate-11));
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  cursor: pointer;
  transition:
    background var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.pend__acao:hover:not(:disabled) {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}

.pend__acao:disabled {
  opacity: 0.5;
  cursor: default;
}

.pend__acao > i {
  width: var(--ico-2);
  height: var(--ico-2);
}

.pend__acao--ico {
  width: 34px;
  padding: 0;
  justify-content: center;
}

.pend__aviso-faixa {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  margin-bottom: calc(var(--esp-2) * -1);
}

.pend__manha {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--esp-4);
  overflow: hidden;
  padding: var(--esp-5) var(--esp-5) var(--esp-5) var(--esp-4);
  border-radius: var(--r-5);
  color: #fff;
  /* O azul-marinho da marca, com luz no alto e o azul da marca acendendo no canto: é a primeira coisa do dia, e
     tem de parecer. O calor fica só no sol (âmbar sobre azul vira cinza — medido). */
  background: radial-gradient(
      90% 120% at 0% 0%,
      rgb(255 255 255 / 0.1) 0%,
      rgb(255 255 255 / 0) 60%
    ),
    radial-gradient(
      70% 90% at 100% 100%,
      rgb(59 130 246 / 0.45) 0%,
      rgb(59 130 246 / 0) 70%
    ),
    linear-gradient(135deg, rgb(15 23 42) 0%, rgb(30 58 138) 100%);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.1),
    0 18px 40px -22px rgb(15 23 42 / 0.75);
  /* Entrada com token e `backwards` (era 420ms cravado com `both`, que prende o estado final no contêiner). */
  animation: pend-manha-entra var(--mov-4) var(--mov-curva) backwards;
}

.pend__manha-sol {
  display: grid;
  place-items: center;
  flex: none;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(160deg, rgb(253 224 71), rgb(245 158 11));
  color: rgb(120 53 15);
  box-shadow:
    0 0 0 6px rgb(251 191 36 / 0.16),
    0 10px 24px -8px rgb(245 158 11 / 0.7);
}

.pend__manha-sol > i {
  width: 28px;
  height: 28px;
}

.pend__manha-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}

.pend__manha-data {
  margin: 0;
  color: rgb(253 230 138);
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
}

.pend__manha-tit {
  margin: 0;
  color: #fff;
  font-size: var(--txt-titulo);
  font-weight: 760;
  letter-spacing: -0.01em;
  line-height: 1.15;
}

.pend__manha-sub {
  margin: 0;
  color: rgb(226 232 240);
  font-size: var(--txt-corpo);
}

.pend__manha-selos {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
  margin: var(--esp-2) 0 0;
  padding: 0;
  list-style: none;
}

.pend__manha-selo {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 4px 10px;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.12);
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.16);
  color: #fff;
  font-size: var(--txt-selo);
  font-weight: var(--peso-forte);
}

.pend__manha-selo--alerta {
  background: rgb(251 191 36);
  box-shadow: none;
  color: rgb(69 26 3);
}

.pend__manha-selo > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

/* A caixinha do rodapé do Bom dia: nota, não botão — a decisão do dia está nos cartões logo abaixo. */
.pend__manha-aviso {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  margin-top: var(--esp-3);
  color: rgb(226 232 240);
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  cursor: pointer;
}

/* ── A CENA DA MANHÃ ────────────────────────────────────────────────────────
   Os tempos saem dos tokens e seguem o roteiro do Briefing do dia (docs/movimento/BRIEFING-CENA.md): uma cena,
   uma vez, camadas sobrepostas. Com "reduzir movimento" os tokens viram 0,01s e o cartão aparece pronto. */
.pend__manha-sol,
.pend__manha-corpo {
  position: relative;
  z-index: 1;
}

/* O SOL NASCE: o ícone sobe de dentro do ladrilho (que o recorta) e o anel âmbar acende em volta. */
.pend__manha-sol {
  overflow: hidden;
  animation: pend-sol-acende var(--mov-5) var(--mov-curva) backwards;
  animation-delay: var(--mov-2);
}

.pend__manha-sol > i {
  animation: pend-sol-nasce var(--mov-4) var(--mov-curva-pulo) backwards;
  animation-delay: calc(var(--mov-2) + var(--cascata-passo) * 2);
}

@keyframes pend-sol-acende {
  from {
    box-shadow:
      0 0 0 0 rgb(251 191 36 / 0),
      0 10px 24px -8px rgb(245 158 11 / 0);
  }
}

@keyframes pend-sol-nasce {
  from {
    opacity: 0;
    transform: translateY(75%);
  }
}

/* O texto sobe em cascata; a saudação nasce de dentro da própria linha; o total rola como odômetro. */
.pend__manha-data,
.pend__manha-sub,
.pend__manha-aviso {
  animation: pend-sobe var(--mov-4) var(--mov-curva) backwards;
}

.pend__manha-data {
  animation-delay: calc(var(--mov-2) + var(--cascata-passo) * 2);
}

.pend__manha-sub {
  --rola-atraso: calc(var(--mov-2) + var(--cascata-passo) * 6);
  animation-delay: calc(var(--mov-2) + var(--cascata-passo) * 6);
}

.pend__manha-aviso {
  animation-delay: calc(var(--mov-3) + var(--mov-4));
}

.pend__manha-tit {
  overflow: hidden;
  margin-bottom: calc(var(--esp-1) * -1);
  padding-bottom: var(--esp-1);
}

.pend__manha-revela {
  display: inline-block;
  animation: pend-revela var(--mov-4) var(--mov-curva) backwards;
  animation-delay: calc(var(--mov-2) + var(--cascata-passo) * 4);
}

/* Os selos chegam um a um — o âmbar dos atrasados primeiro, porque é por onde começar. */
.pend__manha-selo {
  animation: pend-selo-chega var(--mov-3) var(--mov-curva-pulo) backwards;
  animation-delay: calc(
    var(--mov-3) + var(--cascata-passo) * 4 + var(--i, 0) * var(--cascata-passo) *
      2
  );
}

/* A LUZ atravessa o cartão uma vez quando ele já está montado. Em repouso é invisível. */
.pend__manha-luz {
  position: absolute;
  inset: 0 auto 0 0;
  width: 45%;
  background: linear-gradient(
    90deg,
    transparent,
    rgb(255 255 255 / 0.1) 50%,
    transparent
  );
  opacity: 0;
  pointer-events: none;
  animation: pend-luz-passa var(--mov-6) var(--mov-curva) backwards;
  animation-delay: calc(var(--mov-5) + var(--mov-2));
}

/* MEIO COMPASSO: com o cartão da manhã na frente, o mapa e os grupos respondem depois dele. */
.pend--manha .pend__mapa,
.pend--manha .pend__grupo {
  animation: pend-sobe var(--mov-4) var(--mov-curva) backwards;
  animation-delay: calc(var(--mov-3) + var(--mov-2));
}

.pend--manha .pend__grupo {
  animation-delay: calc(var(--mov-3) * 2);
}

@keyframes pend-sobe {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

@keyframes pend-revela {
  from {
    transform: translateY(110%);
  }
}

@keyframes pend-selo-chega {
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.85);
  }
}

@keyframes pend-luz-passa {
  0% {
    opacity: 0;
    transform: translateX(-110%) skewX(-16deg);
  }

  35% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translateX(260%) skewX(-16deg);
  }
}

@keyframes pend-manha-entra {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pend__manha,
  .pend__manha-luz {
    animation: none;
  }
}

.pend__mapa {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
}

.pend__mapa-item {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  padding: 5px 12px 5px 5px;
  border: 0;
  border-radius: 999px;
  background: rgb(var(--solid-1));
  box-shadow:
    inset 0 0 0 1px rgb(var(--slate-4)),
    0 1px 2px rgb(15 23 42 / 0.05);
  color: rgb(var(--slate-12));
  font-size: var(--txt-nota);
  cursor: pointer;
  transition:
    transform var(--mov-1) var(--mov-curva),
    box-shadow var(--mov-1) var(--mov-curva);
}

.pend__mapa-item:hover {
  transform: translateY(-1px);
  box-shadow:
    inset 0 0 0 1px rgb(var(--slate-6)),
    0 6px 14px -8px rgb(15 23 42 / 0.25);
}

.pend__mapa-ico {
  width: 24px;
  height: 24px;
  border-radius: 999px;
}

.pend__mapa-n {
  font-size: var(--txt-corpo);
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}

.pend__mapa-nome {
  color: rgb(var(--slate-11));
}

.pend__avatar {
  display: grid;
  place-items: center;
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  font-size: var(--txt-nota);
  font-weight: 750;
  letter-spacing: 0.02em;
}

.pend__avatar--azul {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

.pend__avatar--teal {
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}

.pend__avatar--iris {
  background: rgb(var(--iris-3));
  color: rgb(var(--iris-11));
}

.pend__avatar--ambar {
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-11));
}

/* O que alguém DISSE (a Andie, a IA, a régua): parece mensagem, com quem falou em cima. */
.pend__fala {
  margin: 0;
  padding: var(--esp-3) var(--esp-4);
  border-radius: 4px var(--r-4) var(--r-4) var(--r-4);
  background: rgb(var(--blue-2));
  box-shadow: inset 0 0 0 1px rgb(var(--blue-4));
}

.pend__fala-rot {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin-bottom: var(--esp-1);
  color: rgb(var(--blue-11));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
}

.pend__fala-rot > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.pend__fala-txt {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.pend__selo {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  align-self: flex-start;
  margin: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
}

.pend__novidade {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: var(--esp-1);
  padding: 4px 10px;
  border: 0;
  border-radius: 999px;
  background: none;
  color: rgb(var(--blue-11));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  cursor: pointer;
}

.pend__novidade:hover {
  background: rgb(var(--blue-2));
}

.pend__novidade > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.pend__atalhos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--esp-2) var(--esp-4);
  margin: 0;
  color: rgb(var(--slate-10));
  font-size: var(--txt-nota);
}

/* O DESFAZER: barra escura no pé da gaveta (o contraste diz "isto é passageiro"), com a linha que esvazia no tempo
   da oferta. Fica presa ao fundo enquanto a lista rola. */
.pend__desfazer {
  position: sticky;
  bottom: var(--esp-3);
  z-index: 3;
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  margin-top: var(--esp-2);
  padding: var(--esp-2) var(--esp-2) var(--esp-2) var(--esp-4);
  overflow: hidden;
  border-radius: 12px;
  background: rgb(var(--slate-12));
  color: rgb(var(--slate-1));
  box-shadow:
    0 12px 28px -10px rgb(0 0 0 / 0.45),
    0 2px 6px rgb(0 0 0 / 0.18);
}

.pend__desfazer-ico {
  flex: none;
  width: 18px;
  height: 18px;
  color: rgb(var(--teal-8, var(--slate-6)));
}

.pend__desfazer-texto {
  flex: 1;
  min-width: 0;
  font-size: var(--txt-corpo, 14px);
  font-weight: 500;
}

.pend__desfazer-btn {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 var(--esp-3);
  border-radius: 9px;
  background: rgb(255 255 255 / 0.12);
  color: inherit;
  font-weight: 600;
  transition: background-color 0.15s ease;
}

.pend__desfazer-btn:hover {
  background: rgb(255 255 255 / 0.2);
}

.pend__desfazer-btn:focus-visible {
  outline: 2px solid rgb(var(--slate-1));
  outline-offset: 2px;
}

.pend__desfazer-btn kbd {
  padding: 0 5px;
  border-radius: 4px;
  background: rgb(255 255 255 / 0.16);
  font-family: inherit;
  font-size: 11px;
  line-height: 18px;
}

.pend__desfazer-tempo {
  position: absolute;
  inset: auto 0 0;
  height: 3px;
  background: rgb(255 255 255 / 0.5);
  transform-origin: left;
  animation: pend-desfazer-tempo var(--pend-desfazer-s, 8s) linear forwards;
}

/* No celular não há teclado: o atalho só ocupa lugar. */
@media (max-width: 639px) {
  .pend__desfazer-btn kbd {
    display: none;
  }
}

@keyframes pend-desfazer-tempo {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

.pend-desfazer-enter-active,
.pend-desfazer-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

.pend-desfazer-enter-from,
.pend-desfazer-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .pend-desfazer-enter-active,
  .pend-desfazer-leave-active {
    transition: opacity 0.15s ease;
  }

  .pend-desfazer-enter-from,
  .pend-desfazer-leave-to {
    transform: none;
  }

  .pend__desfazer-tempo {
    animation: none;
    opacity: 0;
  }
}

.pend__atalhos kbd {
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  margin-inline-end: 2px;
  padding: 0 5px;
  border-radius: 5px;
  background: rgb(var(--solid-1));
  box-shadow:
    inset 0 -1px 0 rgb(var(--slate-6)),
    inset 0 0 0 1px rgb(var(--slate-5));
  color: rgb(var(--slate-12));
  font-family: inherit;
  font-size: 11px;
  font-weight: var(--peso-forte);
}

@media (hover: none) {
  .pend__atalhos {
    display: none;
  }
}

@media (max-width: 639px) {
  /* No celular as ações viram uma fileira que ocupa a largura (medido a 390px: o botão forte caía sozinho). */
  .pend__acoes {
    width: 100%;
    justify-content: flex-start;
  }

  .pend__abrir {
    flex: 1 1 100%;
    justify-content: center;
  }

  /* Toque de 44px (medido 22/10/2026: "Enviar" / "Não enviar" da retomada tinham 34 no celular). */
  .pend__acao {
    height: 44px;
  }

  .pend__acao--ico {
    width: 44px;
  }

  .pend__quem {
    white-space: normal;
  }
}
</style>
